import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { CrudViewContext, ListQuery, Paginated } from '@restavo/crud';
import { BranchScope, tenantIssuePosition } from 'src/services/apis/tenantRequests';
import { IssueEntity } from 'src/services/apis/types';
import { IssueStatusEnum } from 'src/utils/issue';
import { BOARD_LANES, LANE_PAGE_SIZE, resolveNeighbours } from 'src/utils/issueBoard';
import { QUERIES } from 'src/utils/react-query';

type Lane = { rows: IssueEntity[]; total: number; isLoading: boolean };

export interface IssueMove {
  id: string;
  from: IssueStatusEnum;
  to: IssueStatusEnum;
  insertIndex: number;
}

/**
 * Five lane queries over the SAME narrowing the table view has, plus the move mutation.
 *
 * `list` is the resource's own `api.list`, not `tenantIssues` — the route's `type||$eq`
 * injection lives in that closure, not in `compiledQuery`, so
 * calling the client directly would make the board disagree with the tab it renders on.
 */
export function useIssueBoard({
  context,
  list,
}: {
  context: CrudViewContext<IssueEntity, unknown, unknown>;
  list: (scope: BranchScope, query?: ListQuery) => Promise<Paginated<IssueEntity>>;
}) {
  const queryClient = useQueryClient();
  const { params, compiledQuery } = context;
  const scope = params as BranchScope;

  const laneKey = useCallback(
    (status: IssueStatusEnum) => [
      QUERIES.ISSUE_LANES,
      params.orgSlug,
      params.restaurantId,
      params.branchId,
      'lane',
      status,
      compiledQuery,
    ],
    [params.orgSlug, params.restaurantId, params.branchId, compiledQuery]
  );

  const results = useQueries({
    queries: BOARD_LANES.map((status) => ({
      queryKey: laneKey(status),
      queryFn: () =>
        list(scope, {
          ...compiledQuery,
          filter: [...(compiledQuery.filter ?? []), `status||$eq||${status}`],
          sort: ['rank,ASC'],
          page: 1,
          limit: LANE_PAGE_SIZE,
        }),
      keepPreviousData: true,
    })),
  });

  const lanes = BOARD_LANES.reduce<Record<number, Lane>>((accumulator, status, index) => {
    accumulator[status] = {
      rows: results[index].data?.data ?? [],
      total: results[index].data?.total ?? 0,
      isLoading: results[index].isLoading,
    };
    return accumulator;
  }, {});

  const readLane = (status: IssueStatusEnum): Paginated<IssueEntity> =>
    queryClient.getQueryData(laneKey(status)) ?? { data: [], total: 0 };

  const mutation = useMutation(
    async (move: IssueMove) => {
      const destination = readLane(move.to).data.filter((row) => row.id !== move.id);
      const { beforeId, afterId } = resolveNeighbours(
        destination.map((row) => row.id),
        move.insertIndex
      );
      return tenantIssuePosition(scope, move.id, { status: move.to, beforeId, afterId } as never);
    },
    {
      // Optimistic on both lanes: the card leaves one and joins the other in the same tick,
      // so the board never shows it twice or not at all.
      onMutate: async (move: IssueMove) => {
        await Promise.all([queryClient.cancelQueries(laneKey(move.from)), queryClient.cancelQueries(laneKey(move.to))]);

        const from = readLane(move.from);
        const to = readLane(move.to);
        const card = from.data.find((row) => row.id === move.id);
        if (!card) {
          return { from, to };
        }

        const remaining = from.data.filter((row) => row.id !== move.id);
        const destination = (move.from === move.to ? remaining : to.data).filter((row) => row.id !== move.id);
        const inserted = [...destination];
        inserted.splice(move.insertIndex, 0, { ...card, status: move.to });

        if (move.from === move.to) {
          queryClient.setQueryData(laneKey(move.to), { data: inserted, total: to.total });
        } else {
          queryClient.setQueryData(laneKey(move.from), { data: remaining, total: Math.max(0, from.total - 1) });
          queryClient.setQueryData(laneKey(move.to), { data: inserted, total: to.total + 1 });
        }

        return { from, to };
      },
      onError: (_error, move, backup) => {
        if (!backup) {
          return;
        }
        queryClient.setQueryData(laneKey(move.from), backup.from);
        queryClient.setQueryData(laneKey(move.to), backup.to);
      },
      onSettled: (_data, _error, move) => {
        void queryClient.invalidateQueries(laneKey(move.from));
        void queryClient.invalidateQueries(laneKey(move.to));
      },
    }
  );

  return {
    lanes,
    move: mutation.mutate,
    isMoving: mutation.isLoading,
    moveError: mutation.error,
  };
}
